import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, FileText, Video, File, DollarSign, Clock, Calendar } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Course, CourseContent } from "@/types/courseTypes";
import { getCourseById, updateCourse } from "@/utils/firestoreService";
import { useToast } from "@/hooks/use-toast";

const CoursePreview = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [course, setCourse] = useState<Course | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCourse = async () => {
      if (!courseId) return;
      
      setLoading(true);
      try {
        const fetchedCourse = await getCourseById(courseId);
        setCourse(fetchedCourse);
      } catch (error) {
        console.error("Error fetching course:", error);
        toast({
          title: "Error",
          description: "Failed to load course details. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, toast]);

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case "text":
        return <FileText className="h-5 w-5 text-blue-500" />;
      case "video":
        return <Video className="h-5 w-5 text-red-500" />;
      case "file":
      case "quiz":
      default:
        return <File className="h-5 w-5 text-green-500" />;
    }
  };

  const ContentView = ({ content }: { content: CourseContent }) => {
    switch (content.type) {
      case "text":
        return (
          <div className="mt-4 prose max-w-none">
            <p>{content.content}</p>
          </div>
        );
      case "video":
        return (
          <div className="mt-4">
            <div className="aspect-video bg-gray-100 rounded-lg flex items-center justify-center">
              <div className="text-center p-6">
                <Video className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                <p className="text-gray-500">Video content would be embedded here</p>
                <p className="text-sm text-blue-500 mt-2">{content.url}</p>
              </div>
            </div>
          </div>
        );
      case "file":
        return (
          <div className="mt-4">
            <div className="border rounded-lg p-4 flex items-center">
              <File className="h-8 w-8 text-gray-400 mr-3" />
              <div>
                <p className="font-medium">{content.title}</p>
                <p className="text-sm text-gray-500">{content.description}</p>
                <a href={content.url} className="text-sm text-blue-500 hover:underline" target="_blank" rel="noopener noreferrer">
                  View File
                </a>
              </div>
            </div>
          </div>
        );
      case "quiz":
        return (
          <div className="mt-4">
            <div className="border rounded-lg p-4">
              <div className="flex items-center mb-4">
                <FileText className="h-6 w-6 text-purple-500 mr-2" />
                <h3 className="font-medium">Quiz: {content.title}</h3>
              </div>
              <p className="text-gray-600">{content.description}</p>
              <Button className="mt-4" variant="outline">Start Quiz</Button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DashboardLayout role="admin">
        <div className="flex items-center justify-center h-64">
          <p>Loading course...</p>
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout role="admin">
        <div className="flex flex-col items-center justify-center h-64">
          <p className="text-lg">Course not found</p>
          <Button className="mt-4" onClick={() => navigate("/admin-dashboard/courses")}>
            Back to Courses
          </Button>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout role="admin">
      <div className="mb-6">
        <Button 
          variant="ghost" 
          className="mb-4"
          onClick={() => navigate("/admin-dashboard/courses")}
        >
          <ChevronLeft className="h-4 w-4 mr-2" />
          Back to Courses
        </Button>
        
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">{course.title}</h1>
            <div className="flex items-center gap-2 mt-2">
              <Badge 
                className={course.published ? "bg-green-500" : "bg-gray-500"}
              >
                {course.published ? "Published" : "Draft"}
              </Badge>
              <Badge variant="outline">
                {course.type === "theory" ? "Theory" : "Practical"}
              </Badge>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center">
              <DollarSign className="h-5 w-5 text-green-600 mr-1" />
              <span className="text-xl font-bold">${course.price.toFixed(2)}</span>
            </div>
            <div className="flex items-center">
              <Clock className="h-5 w-5 text-blue-500 mr-1" />
              <span>{course.duration} {course.type === "theory" ? "days" : "hours"}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card>
            <CardHeader>
              <CardTitle>Course Description</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-gray-700">{course.description}</p>
              
              {course.type === "theory" && course.contents && course.contents.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-4">Course Content</h3>
                  
                  <Tabs defaultValue={course.contents[0].id} className="mt-6">
                    <TabsList className="mb-4 flex flex-wrap">
                      {course.contents.map((content) => (
                        <TabsTrigger key={content.id} value={content.id} className="flex items-center">
                          {getContentTypeIcon(content.type)}
                          <span className="ml-2">{content.title}</span>
                        </TabsTrigger>
                      ))}
                    </TabsList>
                    
                    {course.contents.map((content) => (
                      <TabsContent key={content.id} value={content.id} className="pt-4 border-t">
                        <h4 className="text-lg font-medium">{content.title}</h4>
                        <p className="text-gray-600 mt-1">{content.description}</p>
                        <ContentView content={content} />
                      </TabsContent>
                    ))}
                  </Tabs>
                </div>
              )}
              
              {course.type === "practical" && (
                <div className="mt-8">
                  <h3 className="text-lg font-medium mb-4">Practical Course Details</h3>
                  <div className="prose max-w-none text-gray-700">
                    <p>This is a practical driving course that requires scheduling and instructor assignment after payment.</p>
                    <p className="mt-4">Students will need to:</p>
                    <ul className="list-disc pl-5 mt-2 space-y-2">
                      <li>Select preferred dates and times</li>
                      <li>Complete a driving experience questionnaire</li>
                      <li>Make payment to confirm booking</li>
                      <li>Be assigned an instructor by the admin</li>
                    </ul>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        
        <div>
          <Card>
            <CardHeader>
              <CardTitle>Course Information</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Course Type</h4>
                  <p>{course.type === "theory" ? "Theory Course" : "Practical Course"}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Duration</h4>
                  <p>{course.duration} {course.type === "theory" ? "days" : "hours"}</p>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Price</h4>
                  <p className="font-semibold">${course.price.toFixed(2)}</p>
                </div>
                
                {course.type === "theory" && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Content Items</h4>
                    <p>{course.contents?.length || 0} items</p>
                  </div>
                )}
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Status</h4>
                  <Badge 
                    className={course.published ? "bg-green-500 mt-1" : "bg-gray-500 mt-1"}
                  >
                    {course.published ? "Published" : "Draft"}
                  </Badge>
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Last Updated</h4>
                  <p>{new Date(course.updatedAt).toLocaleDateString()}</p>
                </div>
                
                <div className="pt-4 border-t mt-6">
                  <h4 className="font-medium mb-3">Admin Actions</h4>
                  <div className="space-y-2">
                    <Button 
                      className="w-full"
                      onClick={() => navigate(`/admin-dashboard/courses?edit=${course.id}`)}
                    >
                      Edit Course
                    </Button>
                    <Button 
                      variant="outline" 
                      className="w-full"
                      onClick={() => navigate(`/admin-dashboard/content?courseId=${course.id}`)}
                    >
                      Manage Content
                    </Button>
                    {!course.published && (
                      <Button 
                        variant="outline" 
                        className="w-full"
                        onClick={async () => {
                          try {
                            await updateCourse(course.id, { published: true });
                            setCourse({ ...course, published: true });
                            toast({
                              title: "Success",
                              description: "Course published successfully",
                            });
                          } catch (error) {
                            console.error("Error publishing course:", error);
                            toast({
                              title: "Error",
                              description: "Failed to publish course",
                              variant: "destructive",
                            });
                          }
                        }}
                      >
                        Publish Course
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default CoursePreview;
